import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import Seo from '../components/Seo';
import { AlertTriangle, ArrowRight, Phone, Mail, MapPin, CheckCircle2, Info } from 'lucide-react';
import FAQAccordion from '../components/FAQAccordion';
import { iconMap } from '../lib/icons';

// ─── Service catalog (12 cards, exact order per spec) ─────────────────────
const SERVICES = [
  {
    id: 'wheelchair',
    icon: 'Accessibility',
    title: 'Wheelchair Transportation',
    description:
      'MoveraCare Mobility provides non-emergency wheelchair transportation in wheelchair-accessible vehicles with trained drivers and proper wheelchair securement. This service is designed for medically stable passengers who can remain safely seated in their wheelchair during transportation.',
    includes: [
      'Manual wheelchair transportation',
      'Power wheelchair transportation, subject to vehicle capacity and advance confirmation',
      'Door-to-door service',
      'Curb-to-curb service',
      'Medical appointments',
      'Rehabilitation appointments',
      'Hospital discharge transportation',
      'Facility transportation',
      'Round-trip transportation',
    ],
    disclaimer: null,
    cta: 'Request Wheelchair Transportation',
  },
  {
    id: 'ambulatory',
    icon: 'Footprints',
    title: 'Ambulatory Transportation',
    description:
      'Professional non-emergency transportation for medically stable passengers who can walk independently or with limited assistance and do not require wheelchair transportation.',
    includes: [
      'Medical appointments',
      'Physical therapy',
      'Diagnostic imaging',
      'Occupational medicine',
      'Follow-up appointments',
      'Hospital discharge',
      'Round-trip transportation',
      'Wait-and-return transportation',
      'Recurring appointments',
    ],
    disclaimer: null,
    cta: 'Request Ambulatory Transportation',
  },
  {
    id: 'workers-comp',
    icon: 'Briefcase',
    title: "Workers' Compensation & Injured Worker Transportation",
    description:
      "MoveraCare Mobility provides dependable transportation for injured workers attending authorized medical appointments throughout Greater Houston. We support workers' compensation adjusters, nurse case managers, employers, third-party administrators, PPO networks, managed care organizations, physicians, and rehabilitation providers.",
    includes: [
      'Physical therapy',
      'Occupational medicine',
      'Orthopedic appointments',
      'Diagnostic imaging',
      'Independent medical examinations',
      'Functional capacity evaluations',
      'Pain management appointments',
      'Rehabilitation appointments',
      'Specialist appointments',
      'Employer-authorized treatment',
      'Recurring appointments',
      'Round-trip transportation',
      'Wait-and-return transportation',
    ],
    disclaimer:
      'Transportation may be arranged by an authorized adjuster, nurse case manager, employer, TPA, PPO, MCO, healthcare provider, transportation broker, or other approved payer representative.',
    cta: 'Request Injured Worker Transportation',
  },
  {
    id: 'oxygen',
    icon: 'Wind',
    title: 'Oxygen-Capable Transportation',
    description:
      'MoveraCare Mobility provides non-emergency transportation for eligible wheelchair and ambulatory passengers traveling with prescribed oxygen. Oxygen-related transportation must be arranged in advance so that passenger needs, vehicle suitability, and service requirements can be confirmed.',
    includes: [
      'Wheelchair passengers traveling with oxygen',
      'Ambulatory passengers traveling with oxygen',
      'Medical appointments',
      'Physical therapy',
      'Rehabilitation',
      'Diagnostic imaging',
      'Dialysis',
      'Specialist appointments',
      'Hospital discharge transportation',
    ],
    disclaimer:
      'MoveraCare does not provide emergency oxygen administration, respiratory therapy, continuous medical monitoring, or ambulance-level care. Passengers must be medically stable and appropriate for non-emergency transportation.',
    cta: 'Request Oxygen-Capable Transportation',
  },
  {
    id: 'hospital-discharge',
    icon: 'Hospital',
    title: 'Hospital Discharge Transportation',
    description:
      'Prearranged non-emergency transportation for medically stable passengers discharged from hospitals, emergency departments, outpatient centers, rehabilitation facilities, and other healthcare locations.',
    includes: [
      'Hospital-to-home transportation',
      'Hospital-to-skilled nursing facility transportation',
      'Hospital-to-assisted living transportation',
      'Hospital-to-group home transportation',
      'Hospital-to-rehabilitation transportation',
      'Wheelchair discharge transportation',
      'Ambulatory discharge transportation',
      'Oxygen-capable discharge transportation when arranged in advance',
    ],
    disclaimer: null,
    cta: 'Request Hospital Discharge Transportation',
    detailLink: '/services/hospital-discharge',
    detailLabel: 'See Hospital Discharge Page',
  },
  {
    id: 'physical-therapy',
    icon: 'Activity',
    title: 'Physical Therapy, IME and FCE Transportation',
    description:
      'Scheduled transportation for patients and injured workers attending physical therapy, rehabilitation, independent medical examinations, functional capacity evaluations, and other authorized medical appointments.',
    includes: [
      'Physical therapy transportation',
      'Occupational therapy transportation',
      'Rehabilitation transportation',
      'IME transportation',
      'FCE transportation',
      'Orthopedic appointments',
      'Diagnostic imaging',
      'Pain management',
      'Recurring appointments',
      'Wait-and-return transportation',
    ],
    disclaimer: null,
    cta: 'Schedule Appointment Transportation',
  },
  {
    id: 'recurring',
    icon: 'Repeat',
    title: 'Recurring and Wait-and-Return Transportation',
    description:
      'MoveraCare can provide recurring transportation for scheduled treatment programs and wait-and-return transportation when a driver is authorized and scheduled to remain available during the appointment.',
    includes: [
      'Weekly therapy appointments',
      'Dialysis appointments',
      'Rehabilitation programs',
      'Occupational medicine appointments',
      "Workers' compensation treatment",
      'Multiple appointments',
      'Round-trip transportation',
      'Standing transportation schedules',
      'Driver waiting service when arranged in advance',
    ],
    disclaimer:
      'Wait-and-return service is subject to authorization, scheduling, driver availability, and applicable wait-time charges.',
    cta: 'Request Recurring Transportation',
  },
  {
    id: 'dialysis',
    icon: 'Droplet',
    title: 'Dialysis Transportation',
    description:
      'Reliable recurring transportation for medically stable wheelchair and ambulatory patients traveling to and from scheduled dialysis appointments.',
    includes: [
      'Recurring weekly transportation',
      'Wheelchair transportation',
      'Ambulatory transportation',
      'Round-trip service',
      'Facility coordination',
      'Family and caregiver communication when authorized',
      'Oxygen-capable transportation when arranged in advance',
    ],
    disclaimer: null,
    cta: 'Schedule Dialysis Transportation',
  },
  {
    id: 'facility',
    icon: 'Building2',
    title: 'Nursing Home, Assisted Living and Group Home Transportation',
    description:
      'Transportation support for residents of skilled nursing facilities, assisted living communities, group homes, rehabilitation centers, adult residential programs, and other healthcare or residential organizations.',
    includes: [
      'Medical appointments',
      'Hospital discharge',
      'Specialist appointments',
      'Diagnostic imaging',
      'Dialysis',
      'Rehabilitation',
      'Approved family outings',
      'Facility-to-facility transportation',
      'Wheelchair and ambulatory transportation',
    ],
    disclaimer: null,
    cta: 'Request Facility Transportation',
  },
  {
    id: 'behavioral',
    icon: 'Brain',
    title: 'Behavioral Health Transportation',
    description:
      'Prearranged non-emergency transportation for medically stable passengers traveling to or from approved behavioral health facilities, outpatient programs, follow-up appointments, and other scheduled healthcare services.',
    includes: null,
    disclaimer:
      'Behavioral health transportation is available only when the passenger is medically and behaviorally appropriate for standard non-emergency transportation. MoveraCare does not provide security transportation, patient restraint, crisis intervention, law-enforcement transportation, clinical supervision, medical monitoring, or involuntary transportation.',
    cta: 'Request Behavioral Health Transportation',
  },
  {
    id: 'private-van',
    icon: 'Car',
    title: 'Private Wheelchair Van and Driver Service',
    description:
      'Reserve a wheelchair-accessible MoveraCare vehicle with a professional driver for extended transportation, multiple stops, medical appointments, facility needs, approved outings, and other prearranged non-emergency transportation.',
    includes: [
      'Professional MoveraCare driver',
      'Wheelchair-accessible vehicle',
      'Multiple scheduled stops',
      'Extended-day service',
      'Medical appointments',
      'Facility transportation',
      'Approved family outings',
      'Community events',
      'Door-to-door or curb-to-curb service',
      'Custom written quote',
    ],
    disclaimer:
      'This is a transportation service provided with a MoveraCare driver. It is not a self-drive vehicle rental.',
    cta: 'Request a Van and Driver Quote',
  },
  {
    id: 'long-distance',
    icon: 'Route',
    title: 'Long-Distance Transportation by Advance Arrangement',
    description:
      'Long-distance wheelchair and ambulatory transportation may be available for approved medical appointments, facility transfers, family needs, and other non-emergency travel.',
    includes: [
      'Advance reservation required',
      'Custom itinerary review',
      'Wheelchair transportation',
      'Ambulatory transportation',
      'Oxygen-capable transportation when approved',
      'Multiple stops',
      'Round-trip service',
      'Written pricing before confirmation',
    ],
    disclaimer:
      'Long-distance service is subject to driver availability, vehicle availability, passenger suitability, mileage, trip duration, lodging requirements, tolls, parking, and final written authorization.',
    cta: 'Request a Long-Distance Quote',
  },
];

// ─── FAQs (14 items per spec) ─────────────────────────────────────────────
const SERVICES_FAQ = [
  {
    question: 'Can a family member or caregiver ride with the patient?',
    answer:
      'A family member, caregiver, or authorized companion may be permitted to ride with the passenger, subject to available seating, vehicle capacity, advance notice, and the terms of the transportation request. Please include companion information when scheduling the trip.',
  },
  {
    question: 'Can MoveraCare transport a power wheelchair?',
    answer:
      "MoveraCare may transport power wheelchairs when the wheelchair's dimensions, weight, passenger needs, and securement requirements are compatible with the assigned vehicle. Advance confirmation is required. Please provide the wheelchair type, approximate dimensions, and total occupied weight when requested.",
  },
  {
    question: 'Do you provide oxygen transportation?',
    answer:
      'MoveraCare provides non-emergency transportation for eligible wheelchair and ambulatory passengers traveling with prescribed oxygen. Oxygen-related transportation must be arranged in advance. MoveraCare does not provide emergency oxygen administration, respiratory therapy, or continuous clinical monitoring.',
  },
  {
    question: 'Can the driver wait during the appointment?',
    answer:
      'Yes. Wait-and-return transportation may be available when requested and authorized in advance. Wait time is subject to driver availability, scheduling requirements, and applicable wait-time charges.',
  },
  {
    question: 'Do you transport patients from hospitals to nursing facilities?',
    answer:
      'Yes. MoveraCare may provide non-emergency hospital discharge transportation to skilled nursing facilities, assisted living communities, rehabilitation facilities, group homes, residences, and other approved destinations for medically stable passengers.',
  },
  {
    question: 'How far in advance should transportation be scheduled?',
    answer:
      'Advance scheduling is recommended, especially for wheelchair, oxygen-capable, recurring, long-distance, hospital discharge, or wait-and-return transportation. Same-day requests may be considered based on driver and vehicle availability.',
  },
  {
    question: 'Do you provide same-day transportation?',
    answer:
      "Same-day transportation may be available, but it is not guaranteed. Availability depends on the passenger's needs, pickup location, destination, required vehicle type, driver availability, and existing trip commitments.",
  },
  {
    question: 'Can a nurse case manager or insurance adjuster book transportation for a patient?',
    answer:
      "Yes. Authorized nurse case managers, workers' compensation adjusters, claims professionals, employers, healthcare providers, transportation brokers, TPAs, PPO networks, MCOs, and other approved representatives may request transportation on behalf of a passenger. Authorization, claim, billing, and appointment information may be required before the trip can be confirmed.",
  },
  {
    question: "What information is required for a workers' compensation trip?",
    answer:
      'Information may include: patient or injured worker name, contact information, pickup address, destination, appointment date and time, return transportation requirements, wheelchair or ambulatory status, oxygen requirements, claim number, date of injury, employer, adjuster, nurse case manager, payer or TPA, purchase order, written authorization, billing instructions, and special transportation requirements. Only information necessary to arrange, document, authorize, and bill the transportation safely will be collected.',
  },
  {
    question: 'Do you offer stretcher transportation?',
    answer:
      'Stretcher transportation is not currently offered unless and until MoveraCare obtains all required licenses, permits, approvals, equipment, vehicle authorizations, and insurance coverage.',
  },
  {
    question: 'Does MoveraCare provide emergency transportation?',
    answer:
      'No. MoveraCare provides non-emergency transportation for medically stable passengers. Call 911 when a passenger is experiencing a medical emergency or requires ambulance-level care.',
  },
  {
    question: 'Can MoveraCare provide recurring transportation?',
    answer:
      "Yes. Recurring transportation may be arranged for dialysis, physical therapy, rehabilitation, workers' compensation treatment, occupational medicine, and other scheduled appointments, subject to availability and authorization.",
  },
  {
    question: 'Do you provide door-to-door transportation?',
    answer:
      "Door-to-door or curb-to-curb service may be available depending on the passenger's mobility needs, the property layout, safety conditions, driver responsibilities, and the confirmed service arrangement.",
  },
  {
    question: 'Does MoveraCare bill insurance directly?',
    answer:
      'Billing depends on the applicable payer, network, transportation broker, employer, claims administrator, facility agreement, purchase order, or authorization. Vendor status with a network does not automatically guarantee that a trip is covered or payable. Contact MoveraCare before service to confirm billing and authorization requirements.',
  },
];

// ─── Reusable notice block ────────────────────────────────────────────────
const NonEmergencyNotice = ({ testId }) => (
  <div
    className="bg-[#F9A825]/10 border-l-4 border-[#F9A825] rounded-lg p-6 md:p-8"
    data-testid={testId}
  >
    <div className="flex items-start gap-4">
      <div className="w-10 h-10 bg-[#F9A825] rounded-full flex items-center justify-center flex-shrink-0">
        <AlertTriangle className="w-5 h-5 text-white" />
      </div>
      <div>
        <h3 className="text-xl md:text-2xl font-bold text-[#004C97] mb-3">
          Important Non-Emergency Transportation Notice
        </h3>
        <div className="space-y-3 text-gray-700 leading-relaxed">
          <p>
            MoveraCare Mobility provides non-emergency transportation for medically stable
            passengers who are appropriate for wheelchair or ambulatory transportation.
          </p>
          <p>
            MoveraCare is <strong>not an ambulance service</strong> and does not provide
            emergency medical care, continuous clinical monitoring, advanced life support, basic
            life support ambulance service, or emergency response.
          </p>
          <p>
            Passengers experiencing chest pain, severe breathing difficulty, uncontrolled
            bleeding, loss of consciousness, stroke symptoms, a medical emergency, or any
            condition requiring immediate medical attention should <strong>call 911</strong>.
          </p>
          <p>
            Transportation requests may be declined or redirected when a passenger&apos;s
            condition requires a higher level of care than MoveraCare is authorized or equipped
            to provide.
          </p>
        </div>
      </div>
    </div>
  </div>
);

// ─── Reusable service card ────────────────────────────────────────────────
const ServiceCard = ({ service, index }) => {
  const IconComponent = iconMap[service.icon];
  const isEven = index % 2 === 0;
  return (
    <Card
      className={`border-2 hover:border-[#F9A825] transition-all ${isEven ? 'bg-white' : 'bg-gray-50'}`}
      data-testid={`service-card-${service.id}`}
    >
      <div className="p-6 md:p-8">
        <CardHeader className="p-0 mb-6">
          <div className="flex items-start space-x-4">
            <div className="w-14 h-14 md:w-16 md:h-16 bg-[#F9A825] rounded-lg flex items-center justify-center flex-shrink-0">
              {IconComponent ? <IconComponent className="w-7 h-7 md:w-8 md:h-8 text-white" /> : null}
            </div>
            <div className="flex-1">
              <CardTitle className="text-xl md:text-2xl mb-2 text-[#004C97] leading-tight">
                {service.title}
              </CardTitle>
              <CardDescription className="text-base leading-relaxed">
                {service.description}
              </CardDescription>
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-0 space-y-5">
          {service.includes ? (
            <div>
              <h4 className="font-semibold text-[#004C97] mb-3">Includes:</h4>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {service.includes.map((item, idx) => (
                  <li key={idx} className="flex items-start space-x-2 text-gray-700">
                    <CheckCircle2 className="w-4 h-4 text-[#F9A825] mt-0.5 flex-shrink-0" />
                    <span className="text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {service.disclaimer ? (
            <div className="bg-gray-100 border-l-4 border-gray-400 rounded p-4">
              <div className="flex items-start gap-2">
                <Info className="w-4 h-4 text-gray-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-gray-700 leading-relaxed">{service.disclaimer}</p>
              </div>
            </div>
          ) : null}

          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <Link to="/book">
              <Button
                className="bg-[#F9A825] text-[#004C97] hover:bg-[#E08E1E] font-semibold w-full sm:w-auto"
                data-testid={`cta-${service.id}`}
              >
                {service.cta}
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            {service.detailLink ? (
              <Link
                to={service.detailLink}
                className="inline-flex items-center justify-center text-[#F9A825] font-semibold hover:text-[#E08E1E] transition-colors px-4 py-2"
                data-testid={`link-${service.id}-detail`}
              >
                {service.detailLabel}
                <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            ) : null}
          </div>
        </CardContent>
      </div>
    </Card>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────
const Services = () => {
  return (
    <div className="min-h-screen pt-20" data-testid="services-page">
      <Seo
        title="Non-Emergency Medical Transportation Services Houston | MoveraCare"
        description="Wheelchair, ambulatory, dialysis, hospital discharge, therapy, workers' compensation, and recurring non-emergency medical transportation services across Greater Houston."
        path="/services"
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Services', url: '/services' },
        ]}
        schema={{
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          name: 'Non-Emergency Medical Transportation Services',
          itemListElement: [
            'Wheelchair Transportation',
            'Ambulatory Transportation',
            "Workers' Compensation Transportation",
            'Dialysis Transportation',
            'Hospital Discharge Transportation',
            'Therapy & Rehabilitation Transportation',
            'Recurring Medical Transportation',
            'Wait-and-Return Transportation',
          ].map((name, idx) => ({ '@type': 'ListItem', position: idx + 1, name })),
        }}
      />
      {/* Hero */}
      <section className="py-20 bg-[#004C97]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">Our Services</h1>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto">
            Non-emergency medical transportation for patients, families, hospitals, facilities,
            workers&apos; compensation programs, and authorized healthcare partners across Greater
            Houston.
          </p>
        </div>
      </section>

      {/* Non-Emergency Notice (top) */}
      <section className="py-10 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <NonEmergencyNotice testId="non-emergency-notice-top" />
        </div>
      </section>

      {/* Service cards */}
      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-8">
            {SERVICES.map((service, index) => (
              <ServiceCard key={service.id} service={service} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Payment & Authorization */}
      <section className="py-16 bg-gray-50" data-testid="payment-authorization-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-[#004C97] mb-4">
              Payment and Transportation Authorization
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto">
              Transportation arrangements and payment requirements depend on who is requesting the
              trip and how the service is authorized.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="border-2 border-gray-100 hover:border-[#F9A825] transition-all">
              <CardHeader>
                <CardTitle className="text-[#004C97]">Private-pay transportation</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed">
                  Patients, families, caregivers, and organizations may request private-pay
                  transportation directly from MoveraCare. Private-pay reservations are not
                  confirmed until availability, pricing, service requirements, and payment terms
                  have been accepted.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-gray-100 hover:border-[#F9A825] transition-all">
              <CardHeader>
                <CardTitle className="text-[#004C97]">Facility transportation</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed">
                  Hospitals, nursing homes, assisted living communities, group homes,
                  rehabilitation facilities, clinics, and healthcare organizations may request
                  transportation under approved facility billing arrangements, purchase orders,
                  service agreements, or advance payment terms.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-gray-100 hover:border-[#F9A825] transition-all">
              <CardHeader>
                <CardTitle className="text-[#004C97]">
                  Workers&apos; compensation transportation
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 mb-3 leading-relaxed">
                  Workers&apos; compensation transportation may require:
                </p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-sm text-gray-700">
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#F9A825] mt-0.5 flex-shrink-0" /><span>Claim number</span></li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#F9A825] mt-0.5 flex-shrink-0" /><span>Adjuster information</span></li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#F9A825] mt-0.5 flex-shrink-0" /><span>Nurse case manager information</span></li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#F9A825] mt-0.5 flex-shrink-0" /><span>Employer information</span></li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#F9A825] mt-0.5 flex-shrink-0" /><span>Date of injury</span></li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#F9A825] mt-0.5 flex-shrink-0" /><span>Authorized appointment details</span></li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#F9A825] mt-0.5 flex-shrink-0" /><span>Purchase order</span></li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#F9A825] mt-0.5 flex-shrink-0" /><span>Written authorization</span></li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#F9A825] mt-0.5 flex-shrink-0" /><span>Billing instructions</span></li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#F9A825] mt-0.5 flex-shrink-0" /><span>Payer or network information</span></li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-2 border-gray-100 hover:border-[#F9A825] transition-all">
              <CardHeader>
                <CardTitle className="text-[#004C97]">Broker or network-authorized transportation</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed">
                  Trips assigned through transportation brokers, PPO networks, MCOs, TPAs, insurance
                  carriers, or other referral partners remain subject to their authorization,
                  pricing, billing, and documentation requirements.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-[#F9A825]/60 bg-[#F9A825]/5 md:col-span-2">
              <CardHeader>
                <CardTitle className="text-[#004C97]">Insurance clarification</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-800 leading-relaxed font-medium">
                  Being listed as a network vendor or accepting an authorized transportation
                  assignment does not guarantee insurance coverage or payment. Coverage,
                  eligibility, authorization, and patient responsibility are determined by the
                  applicable payer, network, employer, claims administrator, or transportation
                  program.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-gray-100 md:col-span-2">
              <CardHeader>
                <CardTitle className="text-[#004C97]">Payment methods</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed">
                  Contact MoveraCare Mobility for current payment and billing options.
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="mt-10 text-center">
            <Link to="/book">
              <Button
                size="lg"
                className="bg-[#F9A825] text-[#004C97] hover:bg-[#E08E1E] font-bold text-lg px-8"
                data-testid="cta-pricing-authorization"
              >
                Request Pricing or Authorization Review
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-16 bg-white" data-testid="service-areas-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#004C97] mb-4">Service Areas</h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Primary */}
            <Card className="border-2 border-[#004C97]/20 hover:border-[#F9A825] transition-all">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#004C97] flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-[#F9A825]" />
                  </div>
                  <CardTitle className="text-[#004C97]">Primary Service Area</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 mb-4 leading-relaxed text-sm">
                  MoveraCare&apos;s primary operating area includes communities where regular
                  wheelchair and ambulatory transportation may be available, subject to scheduling
                  and vehicle availability.
                </p>
                <ul className="space-y-1.5 text-sm text-gray-700">
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#F9A825] mt-0.5 flex-shrink-0" /><span>Richmond</span></li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#F9A825] mt-0.5 flex-shrink-0" /><span>Rosenberg</span></li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#F9A825] mt-0.5 flex-shrink-0" /><span>Sugar Land</span></li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#F9A825] mt-0.5 flex-shrink-0" /><span>Missouri City</span></li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#F9A825] mt-0.5 flex-shrink-0" /><span>Stafford</span></li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#F9A825] mt-0.5 flex-shrink-0" /><span>Southwest Houston</span></li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#F9A825] mt-0.5 flex-shrink-0" /><span>West Houston</span></li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#F9A825] mt-0.5 flex-shrink-0" /><span>Fort Bend County</span></li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#F9A825] mt-0.5 flex-shrink-0" /><span>Selected areas of Harris County</span></li>
                </ul>
              </CardContent>
            </Card>

            {/* Extended */}
            <Card className="border-2 border-[#004C97]/20 hover:border-[#F9A825] transition-all">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#004C97] flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-[#F9A825]" />
                  </div>
                  <CardTitle className="text-[#004C97]">Extended Greater Houston Service Area</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 mb-4 leading-relaxed text-sm">
                  Transportation may also be available in the following communities by advance
                  scheduling:
                </p>
                <ul className="space-y-1.5 text-sm text-gray-700">
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#F9A825] mt-0.5 flex-shrink-0" /><span>Houston</span></li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#F9A825] mt-0.5 flex-shrink-0" /><span>Katy</span></li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#F9A825] mt-0.5 flex-shrink-0" /><span>Pearland</span></li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#F9A825] mt-0.5 flex-shrink-0" /><span>Humble</span></li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#F9A825] mt-0.5 flex-shrink-0" /><span>Kingwood</span></li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#F9A825] mt-0.5 flex-shrink-0" /><span>Tomball</span></li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#F9A825] mt-0.5 flex-shrink-0" /><span>Cypress</span></li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#F9A825] mt-0.5 flex-shrink-0" /><span>Spring</span></li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#F9A825] mt-0.5 flex-shrink-0" /><span>The Woodlands</span></li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#F9A825] mt-0.5 flex-shrink-0" /><span>Pasadena</span></li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#F9A825] mt-0.5 flex-shrink-0" /><span>Baytown</span></li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#F9A825] mt-0.5 flex-shrink-0" /><span>League City</span></li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#F9A825] mt-0.5 flex-shrink-0" /><span>Galveston</span></li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#F9A825] mt-0.5 flex-shrink-0" /><span>Galveston County</span></li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#F9A825] mt-0.5 flex-shrink-0" /><span>Montgomery County</span></li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#F9A825] mt-0.5 flex-shrink-0" /><span>Surrounding Greater Houston communities</span></li>
                </ul>
                <p className="mt-4 text-xs text-gray-600 italic">
                  Extended-area service is subject to trip distance, driver availability, vehicle
                  availability, deadhead mileage, appointment timing, and authorization
                  requirements.
                </p>
              </CardContent>
            </Card>

            {/* Long-distance */}
            <Card className="border-2 border-[#004C97]/20 hover:border-[#F9A825] transition-all">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#004C97] flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-[#F9A825]" />
                  </div>
                  <CardTitle className="text-[#004C97]">Long-Distance Transportation</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 mb-4 leading-relaxed text-sm">
                  Long-distance transportation outside the regular Greater Houston service area may
                  be available by advance arrangement. Each request requires an individual review
                  of:
                </p>
                <ul className="space-y-1.5 text-sm text-gray-700">
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#F9A825] mt-0.5 flex-shrink-0" /><span>Passenger needs</span></li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#F9A825] mt-0.5 flex-shrink-0" /><span>Vehicle type</span></li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#F9A825] mt-0.5 flex-shrink-0" /><span>Total mileage</span></li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#F9A825] mt-0.5 flex-shrink-0" /><span>Trip duration</span></li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#F9A825] mt-0.5 flex-shrink-0" /><span>Driver schedule</span></li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#F9A825] mt-0.5 flex-shrink-0" /><span>Return transportation</span></li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#F9A825] mt-0.5 flex-shrink-0" /><span>Lodging requirements</span></li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#F9A825] mt-0.5 flex-shrink-0" /><span>Tolls</span></li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#F9A825] mt-0.5 flex-shrink-0" /><span>Parking</span></li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#F9A825] mt-0.5 flex-shrink-0" /><span>Oxygen requirements</span></li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#F9A825] mt-0.5 flex-shrink-0" /><span>Final pricing</span></li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#F9A825] mt-0.5 flex-shrink-0" /><span>Payment or authorization</span></li>
                </ul>
              </CardContent>
            </Card>
          </div>

          <div className="mt-10 text-center">
            <Link to="/book">
              <Button
                size="lg"
                className="bg-[#F9A825] text-[#004C97] hover:bg-[#E08E1E] font-bold text-lg px-8"
                data-testid="cta-coverage-review"
              >
                Request a Coverage Review
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-gray-50" data-testid="services-faq-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-[#004C97] mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-gray-700">
              Common questions about MoveraCare Mobility transportation services.
            </p>
          </div>
          <FAQAccordion faqs={SERVICES_FAQ} />
        </div>
      </section>

      {/* Non-Emergency Notice (bottom) */}
      <section className="py-10 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <NonEmergencyNotice testId="non-emergency-notice-bottom" />
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-[#004C97]" data-testid="services-final-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Need Help Choosing the Right Transportation Service?
          </h2>
          <p className="text-xl text-gray-200 mb-8 leading-relaxed">
            Contact MoveraCare Mobility to discuss the passenger&apos;s mobility needs, appointment
            details, service area, authorization, and transportation requirements.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10">
            <Link to="/book">
              <Button
                size="lg"
                className="bg-[#F9A825] text-[#004C97] hover:bg-[#E08E1E] font-bold text-lg px-8 shadow-lg"
                data-testid="cta-final-request"
              >
                Request Transportation
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href="tel:+18329252128">
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-[#004C97] font-semibold text-lg px-8"
                data-testid="cta-final-call"
              >
                <Phone className="mr-2 w-5 h-5" />
                Call Dispatch: 832-925-2128
              </Button>
            </a>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left max-w-2xl mx-auto text-gray-100 text-sm">
            <div className="flex items-start gap-3">
              <Phone className="w-4 h-4 text-[#F9A825] mt-1 flex-shrink-0" />
              <div>
                <p className="text-gray-300 text-xs uppercase tracking-wider">Toll-Free</p>
                <a href="tel:+18329252128" className="hover:text-[#F9A825] transition-colors" data-testid="tel-tollfree">
                  832-925-2128
                </a>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Phone className="w-4 h-4 text-[#F9A825] mt-1 flex-shrink-0" />
              <div>
                <p className="text-gray-300 text-xs uppercase tracking-wider">Local</p>
                <a href="tel:+18329252128" className="hover:text-[#F9A825] transition-colors" data-testid="tel-local">
                  832-925-2128
                </a>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Mail className="w-4 h-4 text-[#F9A825] mt-1 flex-shrink-0" />
              <div>
                <p className="text-gray-300 text-xs uppercase tracking-wider">Email</p>
                <a href="mailto:dispatch@moveracaremobility.com" className="hover:text-[#F9A825] transition-colors" data-testid="email-dispatch">
                  dispatch@moveracaremobility.com
                </a>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <MapPin className="w-4 h-4 text-[#F9A825] mt-1 flex-shrink-0" />
              <div>
                <p className="text-gray-300 text-xs uppercase tracking-wider">Website</p>
                <span>www.MoveraCareMobility.com</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
